import { SectionItem } from '@features/shift-post-v2/components'
import { Colors, Text } from '@pinchenterprisesnpm/friday-ui'
import React from 'react'
import { FormatOptionLabel } from '@src/features/shift-post-v2/steps/shift-details-step/components/location-section/components/format-option-label/format-option-label'
import { debouncedLoadLocationOptions } from '@src/features/shift-post-v2/steps/shift-details-step/components/location-section/location-section.utils'
import { useModal } from '@src/hooks/useModal'
import { EditArrivalInstructionsModal } from '@src/features/shift-post-v2/steps/shift-details-step/components/location-section/modals/edit-arrival-instructions-modal/edit-arrival-instructions-modal'
import { connect } from 'formik'
import { shouldUpdatePayValueBasedOnMinWage } from '@src/helpers/shiftHelpers'
import { useSelector } from 'react-redux'
import {
  selectShiftPostV2CopyEditInitialValues,
  selectShiftPostV2InitialValues,
} from '@src/features/shift-post-v2/state/shift-post-v2.selectors'
import { InfoParagraph } from '@src/features/shift-post-v2/steps/shift-details-step/components/location-section/location-section.styles'
import { ShiftPostPage } from '@src/features/shift-post-v2/pages'
import { ShiftPostStep } from '@src/features/shift-post-v2/steps/steps.types'
import { SeeArrivalInstructionsModal } from '@src/features/shift-post-v2/steps/shift-details-step/components/location-section/modals/see-arrival-instructions-modal/see-arrival-instructions-modal'
import { CustomAsyncSelect } from './components/custom-async-select'
import {
  ILocationSectionProps,
  TLocationDropdownOption,
} from './location-section.types'

export const LocationSection = connect<ILocationSectionProps>(
  ({ sourceStep, formik, sourcePage }) => {
    const isConfirmStep = sourceStep === ShiftPostStep.CONFIRM
    const [isOpen, toggleModal, closeModal] = useModal()
    const [
      isSeeArrivalInstructionsOpen,
      toggleSeeArrivalInstructionsModal,
      closeSeeArrivalInstructionsModal,
    ] = useModal()

    const { setFieldValue, errors, values } = formik

    const copyEditInitialValues = useSelector(
      selectShiftPostV2CopyEditInitialValues,
    )
    const postInitialValues = useSelector(selectShiftPostV2InitialValues)

    const initialValues =
      sourcePage === ShiftPostPage.POST_SHIFT
        ? postInitialValues
        : copyEditInitialValues

    /**
     * Field editing is disabled if template is not selected.
     */
    const editingDisabled =
      sourcePage === ShiftPostPage.POST_SHIFT && !values.template_id

    const locationNotSelected = !values.location

    const onEdit = () => {
      toggleModal()
    }

    const handleLocationChange = (
      location: TLocationDropdownOption | null | undefined,
      customArrivalInstructions?: string,
    ) => {
      const arrivalInstructions =
        customArrivalInstructions ||
        location?.property_arrival_instructions ||
        ''

      setFieldValue('property_id', location?.value)
      setFieldValue('commission_percent', location?.commission_percent)
      setFieldValue('location', {
        ...(location ?? {}),
        // Ensure property_arrival_instructions is never null to prevent HTML processing errors
        property_arrival_instructions:
          location?.property_arrival_instructions || '',
        ...(customArrivalInstructions && {
          property_arrival_instructions: customArrivalInstructions,
        }),
      })
      setFieldValue('min_wage', location?.min_wage)
      setFieldValue('property_timezone', location?.timezone)
      setFieldValue('property_arrival_instructions', arrivalInstructions)
      setFieldValue('suggested_pay_rate', location?.suggested_pay_rate || 0)

      // If the business has pay rate update restriction, then set the pay value to the max of the initial template value and the location's min wage
      let newPayValue = initialValues?.pay_value
      if (
        shouldUpdatePayValueBasedOnMinWage(
          initialValues?.pay_value ?? '0',
          location?.min_wage ?? '0',
        )
      ) {
        newPayValue = location?.min_wage
      }

      setFieldValue('pay_value', newPayValue)
    }

    return (
      <SectionItem
        title="Location"
        hasEdit={true}
        isEditDisabled={locationNotSelected || editingDisabled}
        editDisabledTooltip="Please select a Location first so you can edit the Arrival Instructions."
        onEdit={onEdit}
        onShowFullDescription={
          isConfirmStep ? toggleSeeArrivalInstructionsModal : undefined
        }
        showFullDescriptionTitle="See Arrival Instructions"
      >
        {isConfirmStep && (
          <Text fontSize="medium" mt="xs" mb="0" color={Colors.dark}>
            {values.location?.name ?? '/'}
          </Text>
        )}

        {!isConfirmStep && (
          <>
            <CustomAsyncSelect
              label="Location"
              value={values.location}
              placeholder="Please select a location..."
              isOutlined
              rounded={true}
              size="large"
              loadOptions={debouncedLoadLocationOptions}
              defaultOptions
              my="sm"
              onChange={location => handleLocationChange(location)}
              key={values.keyForLocationsDropdown}
              errorMessage={
                errors.property_id && !values.property_id
                  ? errors.property_id
                  : undefined
              }
              formatOptionLabel={(value: TLocationDropdownOption) => (
                <FormatOptionLabel value={value} />
              )}
              isDisabled={editingDisabled}
            />

            <Text my={0} fontSize="medium">
              Arrival Instructions
            </Text>

            {values.location ? (
              values.location?.property_arrival_instructions ? (
                <InfoParagraph
                  dangerouslySetInnerHTML={{
                    __html: values.location.property_arrival_instructions,
                  }}
                />
              ) : (
                <Text mb={0} mt="sm" color="danger" fontSize="medium">
                  None Provided
                </Text>
              )
            ) : (
              <Text mb={0} mt="sm" color="muted">
                Please select a location first to edit the arrival instructions.
              </Text>
            )}
          </>
        )}

        <EditArrivalInstructionsModal
          isOpen={isOpen}
          closeModal={closeModal}
          isConfirmStep={isConfirmStep}
          handleLocationChange={handleLocationChange}
        />

        <SeeArrivalInstructionsModal
          isOpen={isSeeArrivalInstructionsOpen}
          closeModal={closeSeeArrivalInstructionsModal}
          formik={formik}
        />
      </SectionItem>
    )
  },
)
